﻿/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260407
    This script contains functions used for work with images
*/
'use strict';

import { getTempKey } from './ctssArrayUtils.js';
import {
    allSettled, deletePromise, getErrMsgDivId,
    getJQueryName, savePromise
} from './ctssJQuery.js';
import { setModified } from './ctssVal.js';

const loadImgCtrl = (params = {}) => {
    const preOnloadParams = params.preOnloadParams ?? {};
    const postOnloadParams = params.postOnloadParams ?? {};
    params.preOnload?.(preOnloadParams);
    $(getJQueryName({ ctrlNm: params.imgCtrl })).prop('src', params.fTargetResult);
    setModified(true);
    _hasImgsToUpload = true;
    params.postOnload?.(postOnloadParams);
};

let _hasImgsToDelete = false;
let _hasImgsToUpload = false;

const getImgSizePromise = (params = {}) => {
    if (!params.file && !params.inputFileCtrl) {
        alert('inputFileCtrl or an image file object are required parameters to use getImgSizePromise');
        return;
    }
    const file = params.file ?? $(getJQueryName({ ctrlNm: params.inputFileCtrl }))[0].files[0];

    return new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = (f) => {
            const img = new Image();
            img.src = f.target.result;
            img.onload = () => {
                resolve({ height: img.naturalHeight, width: img.naturalWidth });
            };
        };
        fr.readAsDataURL(file);
    });
};

const deleteImgFromCloudAsync = async (params = {}) => {
    const { imgName, imgsFolder, useTestFolder = false, beforeSendFn, btnId, divErrMsg, loaderId } = params;

    // imgsFolder = 0 is TestFolder, which is falsy, so check for undefined/null only
    if (!imgName || imgsFolder === undefined || imgsFolder === null) {
        alert('imgName and imgsFolder are required parameters to use deleteFromCloudAsync!');
        return;
    }

    return await deletePromise({
        url: `Storage/Img/${imgName}/${imgsFolder}?useTestFolder=${useTestFolder}`,
        beforeSendFn,
        btnId,
        divErrMsg,
        loaderId
    });
};

const initImgCtrl = (params = {}) => {

    const {
        inputFileCtrl = '#editEntityImg',
        imgCtrl = '#imgEntity',
        preOnload,
        preOnloadParams,
        postOnload,
        postOnloadParams
    } = params; 

    $(inputFileCtrl).off('change').on('change', () => {
        uploadToImgCtrl({
            inputFileCtrl,
            imgCtrl,
            preOnload: preOnload,
            preOnloadParams: preOnloadParams,
            postOnload: postOnload,
            postOnloadParams: postOnloadParams
        });
    });
};

const isValidImgAsync = (imgUrl) => {
    if (!imgUrl) return Promise.resolve(false);

    return new Promise((resolve) => {
        const img = new Image();
        
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        
        img.src = imgUrl;
    });
};

const hasImgsToDelete = () => _hasImgsToDelete;
const hasImgsToUpload = () => _hasImgsToUpload;

const processImgSrcError = (e, fallbackUrl) => {

    if (!e || !e.target) return; 

    const imgEl = e.target;

    // Prevent infinite loop
    if (imgEl.dataset.imgErrorHandled === 'true') {
        imgEl.src = '';
        imgEl.alt = '';
        return;
    }

    if (!fallbackUrl) {
        imgEl.src = '';
        imgEl.alt = '';
        return;
    }

    try {
        // Probe the fallback URL using a temporary Image object to avoid assigning a broken src to the DOM element
        const probe = new Image();
        probe.onload = () => {
            imgEl.dataset.imgErrorHandled = 'true'; // Mark as handled
            imgEl.src = fallbackUrl;
        };
        probe.onerror = () => {
            // fallback missing — clear the image and optionally set a placeholder/alt
            imgEl.src = '';
            imgEl.alt = '';
        };
        // Start probe (this triggers load/error handlers)
        probe.src = fallbackUrl;
    } catch (err) {
        // defensive fallback in case of unexpected runtime error
        e.target.src = '';
        console.error('Error in processImgSrcError:', err);
    }
 }

const resizePromise = (params) => {
    return new Promise((resolve) => {
        //let canvas = document.getElementById((params.canvasId === undefined) ? "imgCanvas" : params.canvasId);
        let maxWidth, maxHeight;
        ((params.byHeight !== undefined) && (params.byHeight === true))
            ? maxHeight = (params.maxHeight === undefined) ? 200 : params.maxHeight
            : maxWidth = (params.maxWidth === undefined) ? 200 : params.maxWidth;
        const image = new Image();
        image.onload = () => {

            if ((params.byHeight !== undefined) && (params.byHeight === true)) {
                if ((image.height > maxHeight) ||
                    ((params.forceHeight !== undefined) && (params.forceHeight === true))) {
                    image.height = maxHeight;
                    image.width *= maxHeight / image.height;
                }
            } else {
                if ((image.width > maxWidth) ||
                    ((params.forceWidth !== undefined) && (params.forceWidth === true))) {
                    image.height *= maxWidth / image.width;
                    image.width = maxWidth;
                }
            }
            const canvas = document.createElement('canvas');
            canvas.setAttribute('style', 'position: fixed; left: -2000px;');
            document.body.appendChild(canvas);
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0, image.width, image.height);
            if ((params.useJpg !== undefined) || ((params.useJpeg !== undefined))) {
                resolve(canvas.toDataURL('image/jpeg'));
            } else {
                resolve(canvas.toDataURL());
            }
        };
        image.src = params.frTargetResult;
    });
};

const setHasImgsToDelete = bHasImgsToDelete => _hasImgsToDelete = bHasImgsToDelete;
const setHasImgsToUpload = bHasImgsToUpload => _hasImgsToUpload = bHasImgsToUpload;

const uploadImgToCloudAsync = async (params = {}) => {

    /*  params
        Required
            url: for web api call
            data: for web api call
            imgCtrl: html img control
        Optional
            btnId: Save button Id
            divErrMsg: Name of div displaying any error messages
            loaderId: Loader Id
            postOnload function containing any logic at end of FileReader onload function.
    */

    if (!(_hasImgsToUpload)) return;

    const {
        imgCtrl,
        url,
        imgsFolder,
        useTestFolder = false,
        imgName = null,
        addEncircledImage = false,
        addThumbNail = false,
        isThumbNail = false,
        isDefault = false,
        saveAsJPEG = true,
        resizeImage = true,
        useAspectRatio = true,
        cropImage = false,
        btnId,
        divErrMsg,
        loaderId,
    } = params; 

    /* !imgsFolder can't be used because imgsFolder = 0 is TestFolder which is also a falsy value */
    if (!imgCtrl || !url || imgsFolder === undefined || imgsFolder === null) {
        alert('imgCtrl, url, and imgsFolder are required parameters to use uploadImgToCloudAsync!');
        return;
    }

    const imageData = {
        imgName: imgName,
        isDefault: isDefault,
        saveAsJPEG: saveAsJPEG,
        resizeImage: resizeImage,
        useAspectRatio: useAspectRatio,
        cropImage: cropImage,
        dataURLs: {}
    };

    const imageCtrl = $(getJQueryName({ ctrlNm: imgCtrl }))[0];

    if (isThumbNail) {
        imageData.dataURLs.thumbNailData = imageCtrl.src;
    }
    else {
        imageData.dataURLs.data = imageCtrl.src;
    }

    return await savePromise({
        url,
        data: {
            imgsFolder,
            useTestFolder,
            addEncircledImage,
            addThumbNail,
            imageData
        },
        btnId,
        divErrMsg,
        loaderId
    }).then(data => {
        if (typeof params.postOnload === 'function') {
            const returnData = params.postOnload(data);
            return returnData ?? data;
        }
        _hasImgsToUpload = false;
        return data;
    });
};

const uploadToImgCtrl = (params = {}) => {
    /*  This function is called when uploading an image to an img control

        params
            Required
                inputFileCtrl: html input control with type file
                imgCtrl : imgCtrl
            Optional
                divErrMsg: Name of div displaying any error messages
                preOnload: function containing any logic as start of FileReader onload function.  
                            Returns true if FileReader onload function should proceed.
                postOnload function containing any logic at end of FileReader onload function.
    */

    const {
        inputFileCtrl,
        imgCtrl,
        divErrMsg,
        validImgSizes = [],
        imgSizeErrMsg = 'The selected image does not meet the size requirements.',
    } = params;

    if (!inputFileCtrl || !imgCtrl) {
        alert('inputFileCtrl and imgCtrl are required parameters to use uploadToImgCtrl!');
        return;
    }

    const divErrMsgId = getErrMsgDivId({ divErrMsg: divErrMsg });

    const file = $(getJQueryName({ ctrlNm: inputFileCtrl }))[0].files[0];
    if (!file.type.startsWith("image")) {
        $(divErrMsg).text('Please select an image...').removeClass('hidden');
        return;
    }
    $(divErrMsgId).addClass('hidden');

    const fr = new FileReader();
    fr.onload = async (f) => {
        params.fTargetResult = f.target.result;

        if (Array.isArray(validImgSizes) && validImgSizes.length > 0) {
            const imgSize = await getImgSizePromise({ file });
            const isValidSize = validImgSizes.some(
                ({ height, width }) => imgSize.height === height && imgSize.width === width
            );

            if (!isValidSize) {
                $(divErrMsgId).text(imgSizeErrMsg).removeClass('hidden');
            } else {
                params.imgSize = imgSize;
                loadImgCtrl(params);
            }
        } else {
            loadImgCtrl(params);
        }
    };
    fr.readAsDataURL(file);
};

export {
    deleteImgFromCloudAsync,
    hasImgsToDelete, hasImgsToUpload, initImgCtrl,
    isValidImgAsync,
    processImgSrcError,
    resizePromise,
    setHasImgsToDelete, setHasImgsToUpload,
    uploadImgToCloudAsync, uploadToImgCtrl
}; 